<template>
  <div class="vc-call-root">
    <!-- Connecting overlay - simple version for accepted calls -->
    <v-container
      v-if="!isConnected"
      fluid
      class="pa-0 fill-height bg-backgroundBase vc-incoming-root vc-tv-canvas-bg"
    >
      <PatientBackground />
      
      <!-- Left side: Hero text -->
      <div class="vc-incoming-hero">
        <h1 class="vc-incoming-hero__title">
          Connecting to {{ nurseName }}...
        </h1>
        <div class="vc-incoming-hero__connecting">
          <span class="vc-incoming-hero__connecting-text">Please wait a moment</span>
          <v-progress-circular color="white" indeterminate size="48" width="4" />
        </div>
      </div>
      
      <!-- Right side: White panel with icon -->
      <v-sheet
        class="vc-connecting-panel d-flex flex-column bg-white"
        elevation="24"
        height="100%"
        rounded="0"
      >
        <div class="mb-16">
          <v-icon class="vc-rail__video-icon" color="primary">mdi-video-plus-outline</v-icon>
        </div>
        <div class="flex-grow-1" />
      </v-sheet>
    </v-container>

    <template v-if="isConnected">
      <div class="vc-video-stage">
        <div class="vc-main-video">
          <VideoDisplay :stream="remoteStream" />
          <div class="vc-name-chip vc-name-chip--remote">{{ nurseName }}</div>
          
          <!-- Recording indicator -->
          <v-chip
            v-if="isRecording"
            class="recording-indicator"
            color="error"
            variant="flat"
          >
            <v-icon start size="small">mdi-record-circle</v-icon>
            Recording
          </v-chip>
        </div>
        <div class="vc-pip-video">
          <VideoDisplay :stream="localStream" :mirrored="true" :muted="true" />
          <div class="vc-name-chip vc-name-chip--local">You</div>
        </div>
      </div>

      <div v-if="captionsEnabled && currentCaption" class="vc-caption-panel">
        <p class="vc-caption-text">{{ currentCaption }}</p>
      </div>

      <!-- Audio enable prompt (page refresh scenario) -->
      <div v-if="needsAudioEnable" class="vc-audio-enable-overlay">
        <v-sheet
          class="vc-audio-enable-panel d-flex flex-column align-center"
          elevation="24"
          rounded="xl"
          max-width="600"
          width="90%"
        >
          <v-icon color="warning" size="80" class="mb-4">mdi-volume-high</v-icon>
          <h2 class="text-h4 font-weight-bold text-center mb-4">
            Enable Audio
          </h2>
          <p class="text-body-1 text-center text-medium-emphasis mb-6">
            Audio was paused when the page refreshed.
            <br />
            Click below to enable two-way audio communication.
          </p>
          <VBtnPrimary
            size="x-large"
            class="text-h6"
            @click="handleEnableAudio"
          >
            <v-icon start size="large">mdi-volume-high</v-icon>
            Enable Audio
          </VBtnPrimary>
        </v-sheet>
      </div>

      <div class="vc-bottom-bar">
        <div class="vc-bottom-bar__inner">
          <!-- Caption controls (only show if provider supports captions) -->
          <template v-if="supportsCaptions">
            <template v-if="captionsEnabled">
              <VBtnLight
                class="vc-tray-btn text-none"
                size="x-large"
                @click="handleTurnOffCaptions"
              >
                Turn off closed-captioning
                <v-icon end size="large">mdi-close</v-icon>
              </VBtnLight>

              <VBtnDark
                class="vc-tray-btn text-none"
                size="x-large"
                @click="showCaptionDialog = true"
              >
                {{ captionLanguageLabel }}
                <v-icon end size="large">mdi-chevron-right</v-icon>
              </VBtnDark>
            </template>
            
            <template v-else>
              <VBtnLight
                class="vc-tray-btn text-none"
                size="default"
                @click="showCaptionDialog = true"
              >
                <v-icon start size="small">mdi-closed-caption</v-icon>
                Turn on closed-captioning
              </VBtnLight>
            </template>
          </template>

          <div v-if="supportsCaptions && captionsEnabled" class="vc-bottom-bar__spacer" />

          <div class="vc-device-controls">
            <v-btn
              :color="callStore.isAudioEnabled ? 'white' : 'error'"
              icon
              size="large"
              variant="flat"
              @click="handleToggleAudio"
            >
              <v-icon>{{ callStore.isAudioEnabled ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
            </v-btn>

            <v-btn
              :color="callStore.isVideoEnabled ? 'white' : 'error'"
              icon
              size="large"
              variant="flat"
              @click="handleToggleVideo"
            >
              <v-icon>{{ callStore.isVideoEnabled ? 'mdi-video' : 'mdi-video-off' }}</v-icon>
            </v-btn>

            <span class="vc-call-duration">{{ callDuration }}</span>

            <v-btn color="error" icon size="x-large" variant="flat" @click="handleEndCall">
              <v-icon>mdi-phone-hangup</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </template>

    <CaptionLanguageDialog
      v-model="showCaptionDialog"
      :current-language="captionLanguage"
      @language-selected="handleLanguageSelected"
    />

    <v-dialog v-model="showError" max-width="400" persistent>
      <v-card>
        <v-card-title>Call Error</v-card-title>
        <v-card-text>{{ errorMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <VBtnPrimary @click="handleEndCall">End Call</VBtnPrimary>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { useVideoClient, getZoomClientInstance } from '@virtual-care/shared'
import type { Session } from '@virtual-care/shared'
import { apiClient } from '../services/apiClient'
import { signalingService } from '../services/signalingService'
import VideoDisplay from '../components/VideoDisplay.vue'
import CaptionLanguageDialog from '../components/CaptionLanguageDialog.vue'
import PatientBackground from '../components/PatientBackground.vue'

interface Props {
  sessionId: string
}

const props = defineProps<Props>()
const router = useRouter()
const callStore = useCallStore()

const roomId = apiClient.getRoomId()

// Reactive state for handling page refresh
const isRestoringSession = ref(false)
const sessionRestoreError = ref<string | null>(null)
const wasRestoredFromRefresh = ref(false)

// Initialize provider and credentials (may be null on refresh)
// Use env variable as fallback for provider on page refresh
// NOTE: in-room-endpoint uses Vue CLI, so use process.env.VUE_APP_* (not import.meta.env.VITE_*)
const provider = ref(callStore.provider || process.env.VUE_APP_VIDEO_PROVIDER)
const credentials = ref(callStore.credentials)

// Validate provider is set
if (!provider.value) {
  console.error('[ActiveCallView] No video provider configured in store or environment')
  throw new Error('Video provider not configured. Check VUE_APP_VIDEO_PROVIDER environment variable.')
}

const {
  localStream,
  remoteStream,
  isConnected,
  error: videoError,
  answerCall,
  endCall,
  toggleAudio: toggleAudioTrack,
  toggleVideo: toggleVideoTrack,
  // Caption controls from composable
  captionsEnabled: captionsEnabledFromClient,
  currentCaption,
  supportsCaptions,
  availableCaptionLanguages,
  enableCaptions,
  disableCaptions,
  toggleCaptions,
} = useVideoClient(
  props.sessionId,
  roomId,
  'patient',
  provider.value as never,
  credentials.value as never,
  signalingService
)

const showError = ref(false)
const showCaptionDialog = ref(false)
const captionLanguage = ref('en')
const isEnding = ref(false)
const startTime = ref<number | null>(null)
const callDuration = ref('00:00')
let durationInterval: ReturnType<typeof setInterval> | null = null

// Recording state (read-only for patient)
const isRecording = ref(false)

// Audio enable state (for page refresh scenarios)
const needsAudioEnable = ref(false)

// Use caption state from composable (override local if needed)
const captionsEnabled = computed({
  get: () => captionsEnabledFromClient.value,
  set: (value: boolean) => {
    // Control via composable methods
    if (value && !captionsEnabledFromClient.value) {
      enableCaptions(captionLanguage.value)
    } else if (!value && captionsEnabledFromClient.value) {
      disableCaptions()
    }
  }
})

const nurseName = computed(() => callStore.nurseName ?? 'Virtual Nurse')

const captionLanguageLabel = computed(() => {
  const labels: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'de': 'German',
    'fr': 'French',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'ja': 'Japanese',
    'zh': 'Chinese',
    'ko': 'Korean',
    'it': 'Italian',
    'nl': 'Dutch',
    'ar': 'Arabic',
    'off': 'Off',
  }
  return labels[captionLanguage.value] ?? 'English'
})

const errorMessage = computed(() =>
  videoError.value ?? callStore.error?.message ?? 'An error occurred.'
)

const handleCallEnded = () => {
  if (isEnding.value) return
  isEnding.value = true
  cleanup()
  callStore.endCall()
  router.push('/')
}

function cleanup() {
  if (durationInterval) { clearInterval(durationInterval); durationInterval = null }
  endCall()
}

function startDurationTimer() {
  startTime.value = Date.now()
  durationInterval = setInterval(() => {
    if (startTime.value) {
      const elapsed = Math.floor((Date.now() - startTime.value) / 1000)
      const m = Math.floor(elapsed / 60).toString().padStart(2, '0')
      const s = (elapsed % 60).toString().padStart(2, '0')
      callDuration.value = `${m}:${s}`
    }
  }, 1000)
}

onMounted(async () => {
  // Handle page refresh: restore session if provider not set
  if (!callStore.provider) {
    console.log('[ActiveCallView] No provider in store - restoring session from API')
    isRestoringSession.value = true
    
    try {
      // Join/re-join the session to get credentials
      // If already joined, backend now returns existing credentials
      const joinResponse = await apiClient.joinSession(props.sessionId, 'patient')
      console.log('[ActiveCallView] Join/rejoin response:', joinResponse)
      console.log('[ActiveCallView] Session data:', JSON.stringify(joinResponse.session, null, 2))
      
      if (!joinResponse.session?.provider || !joinResponse.session?.credentials) {
        console.error('[ActiveCallView] Missing fields - provider:', joinResponse.session?.provider, 'credentials:', joinResponse.session?.credentials)
        throw new Error('Invalid session response - missing provider or credentials')
      }
      
      // Populate the call store
      callStore.acceptCall(
        joinResponse.session.provider as never,
        joinResponse.session.credentials as never
      )
      
      // Restore nurse name if available
      if (joinResponse.session.initiatorName) {
        callStore.nurseName = joinResponse.session.initiatorName
      }
      
      // Update reactive refs (provider should already be set from env, but update credentials)
      credentials.value = callStore.credentials
      
      console.log('[ActiveCallView] Session restored successfully, provider:', provider.value, 'has credentials:', !!credentials.value)
      wasRestoredFromRefresh.value = true
      
    } catch (error) {
      console.error('[ActiveCallView] Failed to restore session on refresh:', error)
      sessionRestoreError.value = 'Failed to reconnect to call. Please return to the home screen.'
      
      // Redirect to home after a delay
      setTimeout(() => {
        router.push('/')
      }, 3000)
      return
    } finally {
      isRestoringSession.value = false
    }
  }
  
  // Join the session room to receive call_ended events
  signalingService.joinSession(props.sessionId)
  signalingService.on('call_ended', handleCallEnded)

  try {
    callStore.setStatus('connecting')
    // Pass credentials explicitly in case they were restored on page refresh
    await answerCall(credentials.value)
    callStore.setStatus('connected')
    startDurationTimer()

    // Set up recording event listeners (only for Zoom provider) - read-only for patient
    if (provider.value === 'zoom') {
      try {
        const zoomClient = getZoomClientInstance()
        
        zoomClient.on('recording-started', () => {
          console.log('[ActiveCallView] Recording started')
          isRecording.value = true
        })
        
        zoomClient.on('recording-stopped', () => {
          console.log('[ActiveCallView] Recording stopped')
          isRecording.value = false
        })
        
        console.log('[ActiveCallView] Recording event listeners registered')
        
        // Check if we restored from page refresh - if so, show explicit audio enable prompt
        if (wasRestoredFromRefresh.value) {
          needsAudioEnable.value = true
          console.log('[ActiveCallView] Session was restored from refresh - audio enable prompt will be shown')
        }
        
      } catch (err) {
        console.warn('[ActiveCallView] Failed to set up recording listeners:', err)
      }
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to establish connection'
    console.error('[ActiveCallView] answerCall failed:', message)
    callStore.setError('CALL_FAILED', message)
    showError.value = true
  }
})

onBeforeUnmount(() => {
  signalingService.off('call_ended', handleCallEnded)
  if (!isEnding.value) cleanup()
})

function handleToggleAudio() { toggleAudioTrack(); callStore.toggleAudio() }
function handleToggleVideo() { toggleVideoTrack(); callStore.toggleVideo() }

async function handleLanguageSelected(code: string) {
  try {
    captionLanguage.value = code
    if (code === 'off') {
      await disableCaptions()
    } else {
      await enableCaptions(code)
    }
  } catch (err) {
    console.error('[ActiveCallView] Failed to change caption language:', err)
    const message = err instanceof Error ? err.message : 'Failed to change caption language'
    alert(message)
  }
}

async function handleTurnOffCaptions() {
  try {
    await disableCaptions()
    captionLanguage.value = 'off'
  } catch (err) {
    console.error('[ActiveCallView] Failed to disable captions:', err)
  }
}

async function handleEnableAudio() {
  try {
    console.log('[ActiveCallView] User clicked Enable Audio button')
    const zoomClient = getZoomClientInstance()
    await zoomClient.unmuteAudio()
    console.log('[ActiveCallView] Audio started successfully')
    needsAudioEnable.value = false
  } catch (err) {
    console.error('[ActiveCallView] Failed to enable audio:', err)
    alert('Failed to enable audio. Please try again.')
  }
}

async function handleEndCall() {
  if (isEnding.value) return
  isEnding.value = true
  try {
    await apiClient.endSession(props.sessionId)
    signalingService.notifyCallEnded(props.sessionId, roomId)
  } catch (err) {
    console.error('[ActiveCallView] Error ending session:', err)
  } finally {
    cleanup()
    callStore.endCall()
    router.push('/')
  }
}
</script>

<style scoped>
.recording-indicator {
  position: absolute;
  top: 80px;
  left: 31px;
  z-index: 3;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.vc-audio-enable-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.vc-audio-enable-panel {
  padding: 48px 32px !important;
  text-align: center;
}
</style>
